import {memo} from 'react';

import styled from 'styled-components';

import {getLocalizationService} from '~/shared/services/localisationService';
import {media} from '~/shared/theme/media';
import {headingXS, smallRegular} from '~/shared/theme/typography';
import TenbisCard from '~/shared/components/TenbisCard';
import {flipOnLTR, rotateOnRTL} from '~/shared/theme/utils';
import {MoneyCard} from '~/shared/services/bffService/api/interfaces/responses';
import Button from '~/shared/components/Button';
import {User} from '~/shared/store/models';

import ActionsSection from './ActionsSection';
import ChevronLeft from './assets/chevronLeft.svg?react';

const Root = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  ${media.minLargeMobile`
    gap: 32px;
  `}
  ${media.minLargeTablet`
    gap: 24px;
  `}
`;

const Content = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  ${media.minLargeTablet`
    flex-direction: row-reverse;
    justify-content: space-between;
    gap: auto;
  `}
`;

const TitleContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`;

const Title = styled.h2`
  ${headingXS}
  color: ${({theme}) => theme.colors.contentDefault};
`;

const BackButtonContainer = styled.div`
  ${flipOnLTR`
    padding-left: 16px;
    border-left: 1px solid ${({theme}) => theme.colors.borderStrong};
  `}
`;

const BackButton = styled(Button)`
  display: flex;
  gap: 4px;
`;

const BackButtonArrow = styled(ChevronLeft)`
  ${rotateOnRTL}
`;

const BackButtonText = styled.span`
  ${smallRegular}
  display: block;
`;

const StyledTenbisCard = styled(TenbisCard)`
  ${media.minLargeMobile`
    align-self: center;
  `}
  ${media.minLargeTablet`
    align-self: unset;
  `}
`;

type TenbisCardViewProps = {
  moneycard: MoneyCard;
  onBackButtonClick?: (() => void) | null;
  userData: User | null;
};

const TenbisCardView = ({moneycard, onBackButtonClick, userData}: TenbisCardViewProps) => {
  const {t} = getLocalizationService();

  const cardNumber = `XXXX-XXXX-XXXX-${moneycard.cardLastDigits}`;

  return (
    <Root>
      <TitleContainer>
        {onBackButtonClick && (
          <BackButtonContainer>
            <BackButton onClick={onBackButtonClick}>
              <BackButtonArrow />
              <BackButtonText>
                {t('back_2')}
              </BackButtonText>
            </BackButton>
          </BackButtonContainer>
        )}
        <Title>{t('tenbis_card_xxx', {fourDigits: moneycard.cardLastDigits})}</Title>
      </TitleContainer>
      <Content>
        <StyledTenbisCard cardHolderName={`${userData?.firstName} ${userData?.lastName}`} cardNumber={cardNumber} />
        <ActionsSection />
      </Content>
    </Root>
  );
};

export default memo(TenbisCardView);
